import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ordersAPI, menuAPI } from '../services/api';
import { formatCurrency } from '../utils/format';
import {
  ShoppingCart,
  Clock,
  CheckCircle,
  Activity,
  Sparkles,
  ArrowUp,
  Eye,
  AlertCircle
} from 'lucide-react';

const StaffDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch orders data
  const { data: ordersData, isLoading: ordersLoading } = useQuery(
    'orders',
    () => ordersAPI.getOrders(),
    {
      refetchInterval: 30000,
    }
  );

  const { } = useQuery(
    'dishes',
    () => menuAPI.getDishes()
  );

  const orders = ordersData?.data || [];

  // Calculate statistics for staff
  const pendingOrders = orders.filter(order => order.status === 'pending').length;
  const inProgressOrders = orders.filter(order => order.status === 'in_progress').length;
  const completedOrders = orders.filter(order => order.status === 'completed').length;


  const recentOrders = orders.slice(0, 8);

  // Urgent orders list only includes pending and in_progress orders that are old
  const urgentOrdersList = orders
    .filter(order =>
      (order.status === 'pending' || order.status === 'in_progress') &&
      new Date(order.created_at) < new Date(Date.now() - 15 * 60000)
    )
    .slice(0, 3);

  const stats = [
    {
      icon: ShoppingCart,
      title: t('dashboard.stats.totalOrders', 'Total Orders'),
      value: orders.length,
      changeType: "positive",
      gradient: "from-blue-500 to-cyan-500",
      description: t('dashboard.stats.ordersToday', 'Orders today')
    },
    {
      icon: Clock,
      title: t('dashboard.stats.pendingOrders', 'Pending Orders'),
      value: pendingOrders,
      changeType: "positive",
      gradient: "from-yellow-500 to-amber-500",
      description: t('dashboard.stats.awaitingPreparation', 'Awaiting preparation')
    },
    {
      icon: Activity,
      title: t('dashboard.stats.inProgress', 'In Progress'),
      value: inProgressOrders,
      changeType: "positive",
      gradient: "from-purple-500 to-violet-500",
      description: t('dashboard.stats.beingPrepared', 'Being prepared')
    },
    {
      icon: CheckCircle,
      title: t('dashboard.stats.completed', 'Completed'),
      value: completedOrders,
      changeType: "positive",
      gradient: "from-green-500 to-emerald-500",
      description: t('dashboard.stats.readyForServing', 'Ready for serving')
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Activity className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': t('orders.status.pending', 'pending'),
      'in_progress': t('orders.status.inProgress', 'in progress'),
      'completed': t('orders.status.completed', 'completed'),
      'cancelled': t('orders.status.cancelled', 'cancelled')
    };
    return statusMap[status] || status;
  };

  const getUrgencyLevel = (order) => {
    // Only apply urgency to pending and in_progress orders
    if (order.status !== 'pending' && order.status !== 'in_progress') {
      return 'none';
    }

    const orderTime = new Date(order.created_at);
    const now = new Date();
    const minutesPassed = (now - orderTime) / (1000 * 60);

    if (minutesPassed > 30) return 'high';
    if (minutesPassed > 15) return 'medium';
    return 'low';
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 overflow-x-hidden p-7">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="w-full">
              <div className="flex items-center gap-3 mb-2">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Sparkles className="h-4 w-4" />
                  {t('dashboard.header.staffBadge', 'Staff Dashboard')}
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 break-words">
                {t('dashboard.header.welcome', 'Welcome, {name}! 👨‍🍳').replace('{name}', user?.name || 'Team Member')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm md:text-base max-w-4xl">
                {t('dashboard.header.staffSubtitle', 'Manage and process customer orders efficiently at {restaurant}.')
                  .replace('{restaurant}', user?.restaurant_name || 'your restaurant')}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Order Actions - Moved to top for immediate access */}
        <div className={`mb-8 transition-all duration-700 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 w-full">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">
              {t('dashboard.quickActions.title', 'Quick Actions')}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              <Link
                to="/staff-orders"
                className="group p-3 md:p-4 bg-blue-50 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-blue-200 w-full"
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">
                      {t('dashboard.quickActions.viewOrders', 'Manage All Orders')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">
                      {t('dashboard.quickActions.viewOrdersDesc', 'Process & track all orders')}
                    </p>
                  </div>
                  <ArrowUp className="h-3 w-3 md:h-4 md:w-4 text-gray-400 transform -rotate-45 group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" />
                </div>
              </Link>

              <Link
                to="/staff-orders?status=pending"
                className="group p-3 md:p-4 bg-yellow-50 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-yellow-200 w-full"
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Clock className="h-4 w-4 md:h-5 md:w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">
                      {t('dashboard.stats.pendingOrders', 'Pending Orders')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">
                      {t('dashboard.quickActions.pendingOrdersDesc', 'Review waiting orders')}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full flex-shrink-0">
                    {pendingOrders}
                  </span>
                </div>
              </Link>

              <Link
                to="/staff-orders?status=in_progress"
                className="group p-3 md:p-4 bg-purple-50 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-purple-200 w-full"
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Activity className="h-4 w-4 md:h-5 md:w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">
                      {t('dashboard.stats.inProgress', 'In Progress')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">
                      {t('dashboard.quickActions.inProgressDesc', 'Monitor active orders')}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full flex-shrink-0">
                    {inProgressOrders}
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Urgent Orders Alert - Moved to prominent position below quick actions */}
        {urgentOrdersList.length > 0 && (
          <div className={`mb-8 transition-all duration-700 delay-150 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-red-200 p-4 md:p-6 w-full">
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  {t('dashboard.urgentOrders.title', 'Urgent Orders - Action Required')}
                </h2>
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {urgentOrdersList.length}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {urgentOrdersList.map((order) => (
                  <div
                    key={order.id}
                    className="p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-red-900 text-sm md:text-base">
                        {t('dashboard.recentOrders.orderNumber', 'Order #{number}').replace('{number}', order.id)}
                      </p>
                      <span className="px-2 py-1 bg-red-200 text-red-900 text-xs font-medium rounded-full animate-pulse">
                        {t('orders.urgency.high', 'URGENT')}
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-red-700 mb-2">
                      {t('dashboard.urgentOrders.waitingTime', 'Waiting for {time}min')
                        .replace('{time}', Math.round((new Date() - new Date(order.created_at)) / (1000 * 60)))}
                    </p>
                    <p className="text-xs text-red-600 mb-3">
                      Table {order.table_number} • {order.items?.length || 0} items • {getStatusText(order.status)}
                    </p>
                    <Link
                      to="/staff-orders"
                      className="w-full bg-red-600 hover:bg-red-700 text-white text-center py-2 rounded-lg text-sm font-medium transition-colors duration-300 block"
                    >
                      {t('dashboard.urgentOrders.processNow', 'Process Now')}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards - Moved down to provide context after seeing urgent tasks */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100 w-full"
              >
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r ${stat.gradient} text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                </div>

                <div>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 truncate">
                    {ordersLoading ? '...' : stat.value}
                  </p>
                  <p className="text-sm font-medium text-gray-600 mb-1 md:mb-2 truncate">{stat.title}</p>
                  <p className="text-xs text-gray-500 truncate">{stat.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Orders - Main work section */}
        <div className={`transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 w-full">
            <div className="p-4 md:p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  {t('dashboard.recentOrders.title', 'All Active Orders')}
                </h2>
                <Link
                  to="/staff-orders"
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
                >
                  {t('common.viewAll', 'View all')}
                  <ArrowUp className="h-3 w-3 md:h-4 md:w-4 transform -rotate-45" />
                </Link>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {ordersLoading ? (
                <div className="flex justify-center py-8 md:py-12">
                  <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : recentOrders.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <ShoppingCart className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-2 text-sm md:text-base">
                    {t('dashboard.recentOrders.noOrders', 'No orders yet')}
                  </p>
                  <p className="text-xs md:text-sm text-gray-400">
                    {t('dashboard.recentOrders.noOrdersDesc', 'Orders will appear here as customers place them')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:gap-4">
                  {recentOrders.map((order) => {
                    const urgency = getUrgencyLevel(order);
                    const isActiveOrder = order.status === 'pending' || order.status === 'in_progress';

                    return (
                      <div
                        key={order.id}
                        className={`flex items-center justify-between p-3 md:p-4 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300 group border ${isActiveOrder && urgency === 'high' ? 'bg-red-50 border-red-200' :
                          isActiveOrder && urgency === 'medium' ? 'bg-orange-50 border-orange-200' :
                            order.status === 'completed' ? 'bg-green-50 border-green-200' :
                              order.status === 'cancelled' ? 'bg-gray-100 border-gray-300' :
                                'bg-gray-50 border-gray-200'
                          } w-full`}
                      >
                        <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                          <div className={`p-2 md:p-3 rounded-lg md:rounded-xl border ${getStatusColor(order.status)} flex-shrink-0`}>
                            {getStatusIcon(order.status)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                {t('dashboard.recentOrders.orderNumber', 'Order #{number}').replace('{number}', order.id)}
                              </p>
                              {/* Only show urgency badges for active orders */}
                              {isActiveOrder && urgency === 'high' && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full flex-shrink-0 animate-pulse">
                                  {t('orders.urgency.high', 'URGENT')}
                                </span>
                              )}
                              {isActiveOrder && urgency === 'medium' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full flex-shrink-0">
                                  {t('orders.urgency.medium', 'Waiting')}
                                </span>
                              )}
                              {order.payment_status === 'paid' && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex-shrink-0">
                                  {t('orders.payment.paid', 'Paid')}
                                </span>
                              )}
                              {order.status === 'completed' && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex-shrink-0">
                                  {t('orders.status.completed', 'Completed')}
                                </span>
                              )}
                            </div>
                            <p className="text-xs md:text-sm text-gray-600 truncate">
                              {t('dashboard.recentOrders.tableInfo', 'Table {table} • {amount} • {items} items')
                                .replace('{table}', order.table_number)
                                .replace('{amount}', formatCurrency(order.total_amount))
                                .replace('{items}', order.items?.length || 0)
                              }
                            </p>
                            <div className="flex items-center gap-2 md:gap-4 mt-1 flex-wrap">
                              <span className="text-xs text-gray-500">
                                {new Date(order.created_at).toLocaleTimeString()}
                              </span>
                              {order.estimated_time && isActiveOrder && (
                                <span className="text-xs text-blue-600">
                                  ETA: {order.estimated_time}
                                </span>
                              )}
                              {/* Show waiting time for active orders only */}
                              {isActiveOrder && urgency !== 'none' && (
                                <span className={`text-xs ${urgency === 'high' ? 'text-red-600' :
                                  urgency === 'medium' ? 'text-orange-600' : 'text-gray-500'
                                  }`}>
                                  Waiting: {Math.round((new Date() - new Date(order.created_at)) / (1000 * 60))}min
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} hidden sm:inline-block`}>
                            {getStatusText(order.status)}
                          </span>

                          <Link
                            to={`/staff-orders`}
                            className="p-2 md:p-3 hover:bg-white rounded-lg transition-colors duration-300 opacity-0 group-hover:opacity-100"
                          >
                            <Eye className="h-4 w-4 md:h-5 md:w-5 text-gray-400 hover:text-blue-600" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;